"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { X, ZoomIn, ZoomOut, RotateCcw, Download, Share2, Heart } from "lucide-react"
import { IconButton, Tooltip, Fade } from "@mui/material"

const ImagePopup = ({
  imageUrl = "https://images.unsplash.com/photo-1682687982501-1e58ab814714",
  isOpen = false,
  onClose = () => {},
  altText = "Image",
  allowDownload = false,
  allowShare = true,
  allowLike = false,
}) => {
  const [scale, setScale] = useState(1)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [startPos, setStartPos] = useState({ x: 0, y: 0 })
  const [liked, setLiked] = useState(false)
  const [loading, setLoading] = useState(true)
  const constraintsRef = useRef(null)

  // Reset state when image changes or modal closes
  useEffect(() => {
    setScale(1)
    setPosition({ x: 0, y: 0 })
    setLoading(true)
  }, [imageUrl, isOpen])

  const handleZoomIn = useCallback(() => {
    setScale((prev) => Math.min(prev + 0.25, 3))
  }, [])

  const handleZoomOut = useCallback(() => {
    setScale((prev) => Math.max(prev - 0.25, 0.5))
  }, [])

  const handleReset = useCallback(() => {
    setScale(1)
    setPosition({ x: 0, y: 0 })
  }, [])

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return

      switch (e.key) {
        case "Escape":
          onClose()
          break
        case "+":
          handleZoomIn()
          break
        case "-":
          handleZoomOut()
          break
        case "r":
          handleReset()
          break
        default:
          break
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [isOpen, onClose, handleZoomIn, handleZoomOut, handleReset])

  const handleDragStart = useCallback((e) => {
    if (e.type === "touchstart") {
      setStartPos({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y,
      })
    } else {
      setStartPos({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      })
    }
    setIsDragging(true)
  }, [position.x, position.y])

  const handleDragMove = useCallback((e) => {
    if (!isDragging) return

    let clientX, clientY
    if (e.type === "touchmove") {
      clientX = e.touches[0].clientX
      clientY = e.touches[0].clientY
    } else {
      clientX = e.clientX
      clientY = e.clientY
    }

    setPosition({
      x: clientX - startPos.x,
      y: clientY - startPos.y,
    })
  }, [isDragging, startPos.x, startPos.y])

  const handleDragEnd = useCallback(() => {
    setIsDragging(false)
  }, [])

  const handleDownload = useCallback(() => {
    const link = document.createElement("a")
    link.href = imageUrl
    link.download = "image.jpg"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [imageUrl])

  const handleShare = useCallback(async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: altText,
          text: "Check out this image!",
          url: imageUrl,
        })
      } catch (error) {
        console.log("Error sharing:", error)
      }
    } else {
      navigator.clipboard.writeText(imageUrl)
      alert("Image URL copied to clipboard!")
    }
  }, [altText, imageUrl])

  const handleLike = useCallback(() => {
    setLiked(!liked)
  }, [liked])

  const handleImageLoad = useCallback(() => {
    setLoading(false)
  }, [])

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-90 backdrop-blur-sm"
          style={{ zIndex: 9999 }} // High z-index to ensure it's above everything
          onClick={onClose}
        >
          {/* Close button */}
          <motion.button
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="absolute top-4 right-4 z-[10000] p-2 rounded-full bg-black bg-opacity-50 text-white hover:bg-opacity-70 transition-all"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </motion.button>

          {/* Image container */}
          <div
            className="relative w-full h-full max-w-7xl max-h-[90vh] overflow-hidden"
            ref={constraintsRef}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={handleDragStart}
            onMouseMove={handleDragMove}
            onMouseUp={handleDragEnd}
            onMouseLeave={handleDragEnd}
            onTouchStart={handleDragStart}
            onTouchMove={handleDragMove}
            onTouchEnd={handleDragEnd}
            style={{ 
              cursor: isDragging ? "grabbing" : "grab",
              zIndex: 9999 // Same high z-index as parent
            }}
          >
            {/* Loading spinner */}
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center z-[10001]">
                <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

            {/* Image */}
            <motion.img
              src={imageUrl}
              alt={altText}
              className="w-auto h-auto max-w-full max-h-full object-contain mx-auto select-none"
              style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                opacity: loading ? 0 : 1,
                transition: isDragging ? "none" : "transform 0.2s ease-out, opacity 0.3s ease-in-out",
              }}
              onLoad={handleImageLoad}
              draggable="false"
            />

            {/* Controls */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex items-center gap-2 p-2 rounded-full bg-black bg-opacity-50 backdrop-blur-sm"
              style={{ zIndex: 10000 }} // Higher than the main container
            >
              <Tooltip title="Zoom In" TransitionComponent={Fade} arrow>
                <IconButton onClick={handleZoomIn} className="text-white hover:text-blue-400 transition-colors">
                  <ZoomIn className="w-5 h-5" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Zoom Out" TransitionComponent={Fade} arrow>
                <IconButton onClick={handleZoomOut} className="text-white hover:text-blue-400 transition-colors">
                  <ZoomOut className="w-5 h-5" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Reset" TransitionComponent={Fade} arrow>
                <IconButton onClick={handleReset} className="text-white hover:text-blue-400 transition-colors">
                  <RotateCcw className="w-5 h-5" />
                </IconButton>
              </Tooltip>

              <div className="w-px h-6 bg-white bg-opacity-30 mx-1"></div>

              {allowDownload && (
                <Tooltip title="Download" TransitionComponent={Fade} arrow>
                  <IconButton onClick={handleDownload} className="text-white hover:text-blue-400 transition-colors">
                    <Download className="w-5 h-5" />
                  </IconButton>
                </Tooltip>
              )}

              {allowShare && (
                <Tooltip title="Share" TransitionComponent={Fade} arrow>
                  <IconButton onClick={handleShare} className="text-white hover:text-blue-400 transition-colors">
                    <Share2 className="w-5 h-5" />
                  </IconButton>
                </Tooltip>
              )}

              {allowLike && (
                <Tooltip title={liked ? "Unlike" : "Like"} TransitionComponent={Fade} arrow>
                  <IconButton
                    onClick={handleLike}
                    className={`${liked ? "text-red-500" : "text-white"} hover:text-red-400 transition-colors`}
                  >
                    <Heart className={`w-5 h-5 ${liked ? "fill-current" : ""}`} />
                  </IconButton>
                </Tooltip>
              )}
            </motion.div>

           
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default ImagePopup